import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  approveEducationPlan,
    createEducationPlan,
  deleteEducationPlan,
  deleteEducationPlanAttachment,
  downloadEducationPlanAttachment,
    revokeEducationPlan,
  updateEducationPlan,
  uploadEducationPlanAttachments,
  downloadEducationAnnualPlanPdf,
} from "../../api/education";
import { useCompanyStore } from "../../store/companyStore";
import { APPROVAL_TARGET_EDUCATION_ANNUAL_PLAN } from "../../types/approval";
import { canConfirmInspection } from "../../types/companyRole";
import {
  EducationAnnualPlan,
  EducationAnnualPlanAttachment,
  EducationPlanItem,
  countEducationPlanItems,
  createEmptyEducationPlanItem,
  parseEducationPlanItems,
} from "../../types/education";
import { notifyError, reportError } from "../../utils/notify";
import ApprovalCancelButton from "../approval/ApprovalCancelButton";
import ApprovalRequestModal from "../approval/ApprovalRequestModal";

import "../product/InspectionFormSheet.css";
import "./EducationAnnualPlanTab.css";

type StatusFilter = "ALL" | "DRAFT" | "APPROVED";

type ApprovalStamp = {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  canRevoke?: boolean | null;
  canConfirm?: boolean | null;
  canReopen?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
};

const EMPTY_STAMP: ApprovalStamp = {
  authorName: null,
  reviewerName: null,
  approverName: null,
  canRevoke: false,
  canConfirm: false,
  canReopen: false,
  approvalInProgress: false,
  pendingApprovalRequestId: null,
  canCancelApproval: false,
};

function emptyItem(): EducationPlanItem {
  return createEmptyEducationPlanItem();
}

function formatSize(size?: number | null) {
  if (size == null || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizeStatus(status?: string | null) {
  const s = (status || "DRAFT").toUpperCase();
  if (s === "COMPLETED" || s === "SUBMITTED") return "CONFIRMED";
  return s;
}

function statusLabel(
  status?: string | null,
  approvalInProgress?: boolean | null,
) {
  if (approvalInProgress) return "결재 요청 중";
  const s = normalizeStatus(status);
  if (s === "APPROVED" || s === "CONFIRMED") return "승인";
  return "작성중";
}

function statusBadgeClass(
  status?: string | null,
  approvalInProgress?: boolean | null,
) {
  if (approvalInProgress) return "pending";
  return normalizeStatus(status).toLowerCase();
}

function isDraft(status?: string | null) {
  return normalizeStatus(status) === "DRAFT";
}

function isApproved(status?: string | null) {
  const s = normalizeStatus(status);
  return s === "APPROVED" || s === "CONFIRMED";
}

function stampFrom(row: ApprovalStamp): ApprovalStamp {
  return {
    authorName: row.authorName ?? null,
    reviewerName: row.reviewerName ?? null,
    approverName: row.approverName ?? null,
    canRevoke: row.canRevoke ?? false,
    canConfirm: row.canConfirm ?? false,
    canReopen: row.canReopen ?? false,
    approvalInProgress: row.approvalInProgress ?? false,
    pendingApprovalRequestId: row.pendingApprovalRequestId ?? null,
    canCancelApproval: row.canCancelApproval ?? false,
  };
}

function apiErrorMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message || fallback
  );
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ["작성", authorName],
          ["검토", reviewerName],
          ["승인", approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span
            style={{
              color: value ? "#1f2a37" : "#98a2b3",
              fontWeight: value ? 600 : 400,
            }}
          >
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

type PlanView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; planId: number }
  | { type: "detail"; planId: number };

const LIST_VIEW: PlanView = { type: "list" };

interface Props {
  plans: EducationAnnualPlan[];
  onReload: () => Promise<void>;
  onError: (message: string) => void;
}

function EducationAnnualPlanTab({ plans, onReload, onError }: Props) {
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const fileRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<StatusFilter>("ALL");
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [view, setView] = useState<PlanView>(LIST_VIEW);
  const [planYear, setPlanYear] = useState(String(new Date().getFullYear()));
  const [planTitle, setPlanTitle] = useState("");
  const [planNo, setPlanNo] = useState("");
  const [planNoHint, setPlanNoHint] = useState("");
  const [planStatus, setPlanStatus] = useState("DRAFT");
  const [planItems, setPlanItems] = useState<EducationPlanItem[]>([
    emptyItem(),
  ]);
  const [planRemark, setPlanRemark] = useState("");
  const [attachments, setAttachments] = useState<
    EducationAnnualPlanAttachment[]
  >([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [stamp, setStamp] = useState<ApprovalStamp>(EMPTY_STAMP);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState("");

  const planEditId =
    view.type === "edit" || view.type === "detail" ? view.planId : null;
  const showForm = view.type !== "list";
  const isDetail = view.type === "detail";
  const isEditing = view.type === "create" || view.type === "edit";
  const locked = !isEditing;

  const attachmentsLocked =
    isApproved(planStatus) ||
    Boolean(stamp.approvalInProgress) ||
    (!isEditing && !isDraft(planStatus));

  const summary = useMemo(() => {
    const draft = plans.filter(
      (p) => normalizeStatus(p.status) === "DRAFT",
    ).length;
    const approved = plans.filter((p) => {
      const s = normalizeStatus(p.status);
      return s === "APPROVED" || s === "CONFIRMED";
    }).length;
    return { total: plans.length, draft, approved };
  }, [plans]);

  const filtered = useMemo(() => {
    if (filter === "ALL") return plans;
    if (filter === "APPROVED") {
      return plans.filter((p) => {
        const s = normalizeStatus(p.status);
        return s === "APPROVED" || s === "CONFIRMED";
      });
    }
    return plans.filter((p) => normalizeStatus(p.status) === filter);
  }, [plans, filter]);

  useEffect(() => {
    if (!showForm || planEditId == null) return;
    const latest = plans.find((p) => p.planId === planEditId);
    if (!latest) return;
    if (latest.attachments) setAttachments(latest.attachments);
    setPlanNo(latest.planNo || "");
    setPlanStatus(normalizeStatus(latest.status));
    setStamp(stampFrom(latest));
  }, [plans, planEditId, showForm]);

  const openCreate = () => {
    const year = new Date().getFullYear();
    setPlanYear(String(year));
    setPlanTitle(`${year}년 연간 교육 계획서`);
    setPlanNo("");
    setPlanNoHint("저장 시 문서번호가 자동 발급됩니다.");
    setPlanStatus("DRAFT");
    setPlanItems([emptyItem()]);
    setPlanRemark("");
    setAttachments([]);
    setPendingFiles([]);
    setStamp(EMPTY_STAMP);
    setApprovalOpen(false);
    setApprovalMessage("");
    setView({ type: "create" });
  };

  const fillForm = (row: EducationAnnualPlan) => {
    setPlanYear(String(row.planYear));
    setPlanTitle(row.title || "");
    setPlanNo(row.planNo || "");
    setPlanNoHint("");
    setPlanStatus(normalizeStatus(row.status));
    setPlanItems(parseEducationPlanItems(row.itemsJson));
    setPlanRemark(row.remark || "");
    setAttachments(row.attachments || []);
    setPendingFiles([]);
    setStamp(stampFrom(row));
    setApprovalOpen(false);
    setApprovalMessage("");
  };

  const openDetail = (row: EducationAnnualPlan) => {
    fillForm(row);
    setView({ type: "detail", planId: row.planId });
  };

  const applySaved = (saved: EducationAnnualPlan) => {
    setPlanNo(saved.planNo || "");
    setPlanNoHint("");
    setPlanStatus(normalizeStatus(saved.status));
    setStamp(stampFrom(saved));
    setAttachments(saved.attachments || []);
    setView({ type: "detail", planId: saved.planId });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (locked) {
      reportError(onError, "승인된 계획서는 수정할 수 없습니다.");
      return;
    }
    setSaving(true);
    onError("");
    try {
      const payload = {
        planYear: Number(planYear),
        title: planTitle.trim() || undefined,
        itemsJson: JSON.stringify(planItems),
        remark: planRemark,
      };
      let planId = planEditId;
      if (planId != null) {
        const updated = await updateEducationPlan(planId, payload);
        applySaved(updated);
      } else {
        const created = await createEducationPlan(payload);
        planId = created.planId;
        applySaved(created);
      }
      if (pendingFiles.length > 0 && planId != null) {
        const uploaded = await uploadEducationPlanAttachments(
          planId,
          pendingFiles,
        );
        setAttachments((prev) => [...prev, ...uploaded]);
        setPendingFiles([]);
      }
      await onReload();
    } catch (err: unknown) {
      reportError(
        onError,
        apiErrorMessage(err, "연간 교육 계획서 저장에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUploadNow = async (files: FileList | null) => {
    if (!files?.length || attachmentsLocked) return;
    const selected = Array.from(files);
    if (fileRef.current) fileRef.current.value = "";
    if (planEditId == null) {
      setPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    setSaving(true);
    try {
      const uploaded = await uploadEducationPlanAttachments(
        planEditId,
        selected,
      );
      setAttachments((prev) => [...prev, ...uploaded]);
      await onReload();
    } catch (err: unknown) {
      reportError(
        onError,
        apiErrorMessage(err, "첨부파일 업로드에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (
    attachment: EducationAnnualPlanAttachment,
  ) => {
    if (planEditId == null || attachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    setSaving(true);
    try {
      await deleteEducationPlanAttachment(planEditId, attachment.attachmentId);
      setAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
      await onReload();
    } catch (err: unknown) {
      reportError(
        onError,
        apiErrorMessage(err, "첨부파일 삭제에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (planEditId == null) return;
    if (!canApproveDirect) {
      reportError(onError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "계획서를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      onError("");
      const saved = await approveEducationPlan(planEditId);
      applySaved(saved);
      setApprovalMessage("바로 승인되었습니다.");
      await onReload();
    } catch (err: unknown) {
      reportError(onError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const revoke = async () => {
    if (planEditId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      onError("");
      const saved = await revokeEducationPlan(planEditId);
      applySaved(saved);
      setApprovalMessage("승인이 철회되었습니다.");
      await onReload();
    } catch (err: unknown) {
      reportError(onError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (planEditId == null) return;
    try {
      setPdfDownloading(true);
      const name = planTitle?.trim() || String(planEditId);
      await downloadEducationAnnualPlanPdf(
        planEditId,
        `연간교육계획_${name}.pdf`,
      );
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const renderDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(planStatus) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void handleDownloadPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {isApproved(planStatus) && stamp.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void revoke()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {stamp.approvalInProgress ? (
        <p
          className="quality-form__hint"
          style={{ margin: 0, flexBasis: "100%" }}
        >
          결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고
          다시 요청하세요.
        </p>
      ) : null}
      <ApprovalCancelButton
        detail={stamp}
        className="ncr-btn is-secondary"
        disabled={approving}
        onCancelled={async () => {
          if (planEditId == null) return;
          await onReload();
        }}
      />
      {isDraft(planStatus) && !stamp.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => {
              if (planEditId == null) return;
              setView({ type: "edit", planId: planEditId });
            }}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage("");
              setApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void approve()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => {
              if (planEditId == null) return;
              if (!window.confirm("삭제할까요?")) return;
              void deleteEducationPlan(planEditId).then(() => {
                setView({ type: "list" });
                void onReload();
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() => setView({ type: "list" })}
      >
        목록
      </button>
    </div>
  );

  const renderEditActions = () => (
    <div className="quality-form__actions">
      <button
        type="submit"
        className="ncr-btn is-primary"
        disabled={saving}
      >
        {saving ? "저장 중..." : "저장"}
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          view.type === "edit" && planEditId != null
            ? setView({ type: "detail", planId: planEditId })
            : setView({ type: "list" })
        }
      >
        {view.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  if (showForm) {
    return (
      <form
        className="edu-plan-form"
        onSubmit={isEditing ? save : (e) => e.preventDefault()}
      >
        {isDetail ? (
          <div className="quality-shell__toolbar" style={{ marginBottom: 12 }}>
            <div>
              <h3 style={{ margin: 0 }}>연간 교육 계획서 상세</h3>
              <p className="quality-form__hint" style={{ margin: "4px 0 0" }}>
                {planTitle || "-"} ·{" "}
                {statusLabel(planStatus, stamp.approvalInProgress)}
              </p>
            </div>
            {renderDetailActions()}
          </div>
        ) : (
          renderEditActions()
        )}

        {approvalMessage ? (
          <p className="quality-form__hint" style={{ color: "#1a5fb4" }}>
            {approvalMessage}
          </p>
        ) : null}

        <div className="edu-plan-form__header">
          <div>
            <h3>
              {view.type === "create"
                ? "연간 교육 계획서 작성"
                : view.type === "edit"
                  ? "연간 교육 계획서 수정"
                  : "연간 교육 계획서"}
            </h3>
            <p>
              {isEditing
                ? "저장하면 상세보기로 이동합니다."
                : statusLabel(planStatus, stamp.approvalInProgress)}
            </p>
          </div>
          <StampBox
            authorName={stamp.authorName}
            reviewerName={stamp.reviewerName}
            approverName={stamp.approverName}
          />
        </div>

        <section className="edu-plan-card">
          <div className="edu-plan-card__title">
            <h4>문서 정보</h4>
            <span
              className={`quality-badge is-${statusBadgeClass(planStatus, stamp.approvalInProgress)}`}
            >
              {statusLabel(planStatus, stamp.approvalInProgress)}
            </span>
          </div>
          <div className="edu-plan-grid">
            <label>
              문서번호
              <input
                value={view.type === "create" ? "" : planNo}
                readOnly
                disabled
                placeholder={
                  view.type === "create" ? "저장 시 자동 발급" : undefined
                }
              />
              {view.type === "create" && planNoHint ? (
                <span className="quality-form__hint">{planNoHint}</span>
              ) : null}
            </label>
            <label>
              연도
              <input
                type="number"
                min={2000}
                max={2100}
                value={planYear}
                disabled={planEditId != null || locked}
                onChange={(e) => setPlanYear(e.target.value)}
                required
              />
            </label>
            <label className="is-span-2">
              제목
              <input
                value={planTitle}
                disabled={locked}
                onChange={(e) => setPlanTitle(e.target.value)}
                placeholder="예: 2026년 연간 교육 계획서"
              />
            </label>
          </div>
        </section>

        <section className="edu-plan-card">
          <div className="edu-plan-card__title">
            <div>
              <h4>교육 계획 항목</h4>
              <p className="edu-plan-card__sub">
                과정명과 세부내용을 한 줄로 입력합니다. · {planItems.length}건
              </p>
            </div>
            {!locked ? (
              <button
                type="button"
                className="ncr-btn is-secondary"
                onClick={() => setPlanItems((prev) => [...prev, emptyItem()])}
              >
                항목 추가
              </button>
            ) : null}
          </div>
          <div className="edu-plan-table-wrap">
            <table className="edu-plan-table">
              <thead>
                <tr>
                  <th className="col-no">No</th>
                  <th className="col-type">구분</th>
                  <th className="col-course">교육과정명</th>
                  <th className="col-detail">세부교육내용</th>
                  <th className="col-target">대상</th>
                  <th className="col-period">시기</th>
                  <th className="col-hours">시간</th>
                  <th className="col-method">방법</th>
                  <th className="col-owner">담당</th>
                  {!locked ? <th className="col-actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {planItems.map((item, index) => (
                  <tr key={item.id}>
                    <td className="is-center col-no">{index + 1}</td>
                    <td className="col-type">
                      <select
                        className={
                          item.educationType === "EXTERNAL"
                            ? "is-external"
                            : "is-internal"
                        }
                        value={item.educationType || "INTERNAL"}
                        disabled={locked}
                        onChange={(e) =>
                          setPlanItems((prev) =>
                            prev.map((r) =>
                              r.id === item.id
                                ? { ...r, educationType: e.target.value }
                                : r,
                            ),
                          )
                        }
                      >
                        <option value="INTERNAL">사내</option>
                        <option value="EXTERNAL">사외</option>
                      </select>
                    </td>
                    <td className="col-course">
                      <input
                        value={item.courseName}
                        disabled={locked}
                        onChange={(e) =>
                          setPlanItems((prev) =>
                            prev.map((r) =>
                              r.id === item.id
                                ? { ...r, courseName: e.target.value }
                                : r,
                            ),
                          )
                        }
                        placeholder="예: 품질경영시스템 인식교육"
                      />
                    </td>
                    <td className="col-detail">
                      <input
                        value={item.detailContent}
                        disabled={locked}
                        onChange={(e) =>
                          setPlanItems((prev) =>
                            prev.map((r) =>
                              r.id === item.id
                                ? { ...r, detailContent: e.target.value }
                                : r,
                            ),
                          )
                        }
                        placeholder="세부 교육 내용"
                      />
                    </td>
                    <td className="col-target">
                      <input
                        value={item.target}
                        disabled={locked}
                        onChange={(e) =>
                          setPlanItems((prev) =>
                            prev.map((r) =>
                              r.id === item.id
                                ? { ...r, target: e.target.value }
                                : r,
                            ),
                          )
                        }
                        placeholder="전 직원"
                      />
                    </td>
                    <td className="col-period">
                      <input
                        value={item.period}
                        disabled={locked}
                        onChange={(e) =>
                          setPlanItems((prev) =>
                            prev.map((r) =>
                              r.id === item.id
                                ? { ...r, period: e.target.value }
                                : r,
                            ),
                          )
                        }
                        placeholder="1분기"
                      />
                    </td>
                    <td className="col-hours">
                      <input
                        value={item.hours}
                        disabled={locked}
                        onChange={(e) =>
                          setPlanItems((prev) =>
                            prev.map((r) =>
                              r.id === item.id
                                ? { ...r, hours: e.target.value }
                                : r,
                            ),
                          )
                        }
                        placeholder="2H"
                      />
                    </td>
                    <td className="col-method">
                      <input
                        value={item.method}
                        disabled={locked}
                        onChange={(e) =>
                          setPlanItems((prev) =>
                            prev.map((r) =>
                              r.id === item.id
                                ? { ...r, method: e.target.value }
                                : r,
                            ),
                          )
                        }
                        placeholder="집합"
                      />
                    </td>
                    <td className="col-owner">
                      <input
                        value={item.owner}
                        disabled={locked}
                        onChange={(e) =>
                          setPlanItems((prev) =>
                            prev.map((r) =>
                              r.id === item.id
                                ? { ...r, owner: e.target.value }
                                : r,
                            ),
                          )
                        }
                        placeholder="담당자"
                      />
                    </td>
                    {!locked ? (
                      <td className="is-center col-actions">
                        <button
                          type="button"
                          className="edu-plan-item__del"
                          aria-label={`${index + 1}번 항목 삭제`}
                          onClick={() =>
                            setPlanItems((prev) => {
                              const next = prev.filter((r) => r.id !== item.id);
                              return next.length ? next : [emptyItem()];
                            })
                          }
                        >
                          삭제
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!locked ? (
            <div className="edu-plan-table-footer">
              <button
                type="button"
                className="ncr-btn is-secondary"
                onClick={() => setPlanItems((prev) => [...prev, emptyItem()])}
              >
                항목 추가
              </button>
            </div>
          ) : null}
        </section>

        <section className="edu-plan-card">
          <div className="edu-plan-card__title">
            <h4>첨부파일</h4>
            {!attachmentsLocked ? (
              <button
                type="button"
                className="ncr-btn is-secondary"
                disabled={saving}
                onClick={() => fileRef.current?.click()}
              >
                파일 추가
              </button>
            ) : null}
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              onChange={(e) => void handleUploadNow(e.target.files)}
            />
          </div>
          <p className="edu-plan-hint">
            교육 계획 근거 문서, 승인본 PDF 등을 첨부할 수 있습니다.
            {planEditId == null
              ? " 신규 작성 시 저장과 함께 업로드됩니다."
              : ""}
            {attachmentsLocked
              ? " 승인 후에는 첨부를 변경할 수 없습니다."
              : ""}
          </p>
          <ul className="edu-plan-files">
            {attachments.map((file) => (
              <li key={file.attachmentId}>
                <div>
                  <strong>{file.fileName}</strong>
                  <span>{formatSize(file.fileSize)}</span>
                </div>
                <div className="edu-plan-files__actions">
                  <button
                    type="button"
                    className="ncr-btn is-secondary"
                    onClick={() =>
                      void downloadEducationPlanAttachment(
                        planEditId!,
                        file.attachmentId,
                        file.fileName,
                      ).catch(() =>
                        reportError(
                          onError,
                          "첨부파일 다운로드에 실패했습니다.",
                        ),
                      )
                    }
                  >
                    다운로드
                  </button>
                  {!attachmentsLocked ? (
                    <button
                      type="button"
                      className="ncr-btn is-danger"
                      disabled={saving}
                      onClick={() => void handleDeleteAttachment(file)}
                    >
                      삭제
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
            {pendingFiles.map((file, idx) => (
              <li key={`pending-${file.name}-${idx}`}>
                <div>
                  <strong>{file.name}</strong>
                  <span>저장 대기 · {formatSize(file.size)}</span>
                </div>
                <button
                  type="button"
                  className="ncr-btn is-danger"
                  onClick={() =>
                    setPendingFiles((prev) => prev.filter((_, i) => i !== idx))
                  }
                >
                  제거
                </button>
              </li>
            ))}
            {attachments.length === 0 && pendingFiles.length === 0 && (
              <li className="is-empty">등록된 첨부파일이 없습니다.</li>
            )}
          </ul>
        </section>

        <section className="edu-plan-card">
          <div className="edu-plan-card__title">
            <h4>비고</h4>
          </div>
          <textarea
            rows={3}
            value={planRemark}
            disabled={locked}
            onChange={(e) => setPlanRemark(e.target.value)}
            placeholder="특이사항, 참고사항"
          />
        </section>

        {isDetail ? (
          <div className="quality-form__actions">{renderDetailActions()}</div>
        ) : (
          renderEditActions()
        )}

        {planEditId != null ? (
          <ApprovalRequestModal
            open={approvalOpen}
            titleDefault={`${planTitle || "연간 교육 계획서"} 결재`}
            targetType={APPROVAL_TARGET_EDUCATION_ANNUAL_PLAN}
            targetId={planEditId}
            onClose={() => setApprovalOpen(false)}
            onSubmitted={() => {
              setApprovalOpen(false);
              setApprovalMessage(
                "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
              );
              void onReload();
            }}
          />
        ) : null}
      </form>
    );
  }

  return (
    <div className="edu-plan">
      <div className="edu-plan__toolbar">
        <div>
          <h3>연간 교육 계획서</h3>
          <p>연도별 교육 계획과 첨부 문서를 관리합니다.</p>
        </div>
        <button
          type="button"
          className="ncr-btn is-primary"
          onClick={openCreate}
        >
          연도 추가
        </button>
      </div>

      <div className="edu-plan__summary">
        {(
          [
            ["ALL", "전체", summary.total],
            ["DRAFT", "작성중", summary.draft],
            ["APPROVED", "승인", summary.approved],
          ] as const
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            className={`edu-plan__chip${filter === key ? " is-active" : ""} is-${key.toLowerCase()}`}
            onClick={() => setFilter(key)}
          >
            <strong>{count}</strong>
            <span>{label}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="edu-plan__empty">
          <p>등록된 연간 교육 계획서가 없습니다.</p>
          <button
            type="button"
            className="ncr-btn is-primary"
            onClick={openCreate}
          >
            첫 계획서 작성
          </button>
        </div>
      ) : (
        <div className="edu-plan__list">
          {filtered.map((row) => {
            const status = normalizeStatus(row.status);
            return (
              <article
                key={row.planId}
                className="edu-plan__card"
                onClick={() => openDetail(row)}
              >
                <div className="edu-plan__card-main">
                  <div className="edu-plan__year">{row.planYear}</div>
                  <div>
                    <h4>{row.title || `${row.planYear}년 연간 교육 계획서`}</h4>
                    <p>
                      {row.planNo ? `${row.planNo} · ` : ""}
                      항목 {countEducationPlanItems(row.itemsJson)}개 · 첨부{" "}
                      {row.attachmentCount ?? 0}개
                      {row.authorName ? ` · 작성 ${row.authorName}` : ""}
                    </p>
                  </div>
                </div>
                <div className="edu-plan__card-side">
                  <span
                    className={`quality-badge is-${statusBadgeClass(status, row.approvalInProgress)}`}
                  >
                    {statusLabel(status, row.approvalInProgress)}
                  </span>
                  {status === "DRAFT" && !row.approvalInProgress ? (
                    <button
                      type="button"
                      className="ncr-btn is-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          !window.confirm(
                            `${row.planYear}년 계획서를 삭제할까요?`,
                          )
                        )
                          return;
                        void deleteEducationPlan(row.planId)
                          .then(onReload)
                          .catch(() =>
                            reportError(onError, "삭제에 실패했습니다."),
                          );
                      }}
                    >
                      삭제
                    </button>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: 12 }}>-</span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default EducationAnnualPlanTab;
